import { Box, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, SelectChangeEvent, Typography } from "@mui/material";
import { PhotoEvent, Pricelist } from 'common-models';
import { FC, ReactNode } from 'react';

interface EventConfigurationPanelProps {
    photoEvent: PhotoEvent;
    eventIsSubEvent: boolean;
    allEvents: PhotoEvent[];
    allPricelists: Pricelist[] | undefined;
    onEventIsSubEventChanged: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onParentEventChanged: (event: SelectChangeEvent<string>, child: ReactNode) => void;
    onPricelistChanged: (event: SelectChangeEvent<string>, child: ReactNode) => void;
    getEventTitle: (event: PhotoEvent) => string;
}

const EventConfigurationPanel: FC<EventConfigurationPanelProps> = ({
    photoEvent,
    eventIsSubEvent,
    allEvents,
    allPricelists,
    onEventIsSubEventChanged,
    onParentEventChanged,
    onPricelistChanged,
    getEventTitle
}) => {
    return (
        <>
            <Typography variant="h3" gutterBottom>
                Configuration
            </Typography>

            {/* Sub Event Configuration */}
            <Box sx={{ mb: 3 }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            id='event-issubevent'
                            onChange={onEventIsSubEventChanged}
                            checked={eventIsSubEvent}
                        />
                    }
                    label='This is a sub-event'
                />
                <Typography variant="caption" display="block" color="text.secondary">
                    Sub-events are part of a larger main event
                </Typography>
            </Box>

            {eventIsSubEvent && (
                <Box sx={{ mb: 3 }}>
                    <FormControl fullWidth>
                        <InputLabel id='select-parent-event-label'>Parent Event</InputLabel>
                        <Select
                            labelId='select-parent-event-label'
                            variant='outlined'
                            onChange={onParentEventChanged}
                            value={photoEvent.parentEventId || ''}
                            label="Parent Event"
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        bgcolor: (theme) => theme.palette.background.paper,
                                        color: (theme) => theme.palette.text.primary,
                                    },
                                },
                                MenuListProps: {
                                    sx: {
                                        color: (theme) => theme.palette.text.primary,
                                    },
                                },
                            }}
                        >
                            <MenuItem value='' disabled>
                                Select parent event
                            </MenuItem>
                            {allEvents
                                ?.filter(x => x.id !== photoEvent.id)
                                ?.map(x =>
                                    <MenuItem
                                        key={`parent-event-${x.id}`}
                                        value={x.id}
                                    >
                                        {getEventTitle(x)}
                                    </MenuItem>
                                )
                            }
                        </Select>
                    </FormControl>
                </Box>
            )}

            {/* Pricing Configuration */}
            {!photoEvent.isFreeOfCharge && (
                <Box sx={{ mb: 3 }}>
                    <FormControl fullWidth>
                        <InputLabel id='select-pricelist-label'>Price List</InputLabel>
                        <Select
                            labelId='select-pricelist-label'
                            variant='outlined'
                            onChange={onPricelistChanged}
                            value={photoEvent.priceListId || ''}
                            label="Price List"
                        >
                            <MenuItem value='' disabled>
                                Select price list
                            </MenuItem>
                            {allPricelists?.map(x =>
                                <MenuItem
                                    key={`pricelist-${x.id}`}
                                    value={x.id}
                                >
                                    {x.name}
                                </MenuItem>
                            )}
                        </Select>
                    </FormControl>
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                        Choose pricing structure for photo sales
                    </Typography>
                </Box>
            )}
        </>
    );
};

export default EventConfigurationPanel;
